import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createGeneration } from '../api/client';
import type { GenerationType } from '../api/types';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import '../components/ui/ui-input';
import '../components/ui/ui-select';
import { alertStyles, boxSizingStyles } from '../styles/lit-shared';

@customElement('content-create-form')
export class ContentCreateForm extends LitElement {
  static styles = [
    boxSizingStyles,
    alertStyles,
    css`
      :host {
        display: block;
      }

      form {
        display: flex;
        flex-direction: column;
        gap: var(--space-lg);
      }

      .hint-block {
        padding: var(--space-sm) var(--space-md);
        border-radius: var(--radius-sm);
        background: rgb(139 92 246 / 8%);
        border: 1px solid rgb(139 92 246 / 20%);
        font-size: 0.8125rem;
        color: var(--color-text-muted);
        line-height: 1.55;
      }

      .hint-block strong {
        color: var(--color-primary-light);
        font-weight: 600;
      }

      .field-group {
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
      }

      .divider {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        color: var(--color-text-subtle);
        font-size: 0.8125rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .divider::before,
      .divider::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--color-border);
      }
    `,
  ];

  @state() private type: GenerationType = 'e-commerce';
  @state() private url = '';
  @state() private scrapedTitle = '';
  @state() private scrapedDescription = '';
  @state() private loading = false;
  @state() private error = '';

  private typeOptions = [
    { value: 'e-commerce', label: 'E-commerce (producto de tienda)' },
    { value: 'general', label: 'General (artículo o tema libre)' },
  ];

  private handleTypeChange(e: CustomEvent<{ value: string }>) {
    this.type = e.detail.value as GenerationType;
    this.error = '';
  }

  private handleUrlChange(e: CustomEvent<{ value: string }>) {
    this.url = e.detail.value;
  }

  private handleTitleChange(e: CustomEvent<{ value: string }>) {
    this.scrapedTitle = e.detail.value;
  }

  private handleDescriptionChange(e: CustomEvent<{ value: string }>) {
    this.scrapedDescription = e.detail.value;
  }

  private validate(): string | null {
    if (this.type === 'e-commerce') {
      if (!this.url.trim()) return 'La URL es obligatoria para el tipo e-commerce.';
      try {
        new URL(this.url.trim());
      } catch {
        return 'La URL no es válida. Incluye http:// o https://';
      }
      const hasTitle = !!this.scrapedTitle.trim();
      const hasDesc = !!this.scrapedDescription.trim();
      if (hasTitle !== hasDesc) {
        return 'Debes proporcionar título y descripción juntos, o dejar ambos vacíos.';
      }
    } else {
      if (!this.scrapedTitle.trim()) return 'El título es obligatorio para el tipo general.';
      if (!this.scrapedDescription.trim()) return 'La descripción es obligatoria para el tipo general.';
    }
    return null;
  }

  async submit(): Promise<void> {
    const validationError = this.validate();
    if (validationError) {
      this.error = validationError;
      return;
    }

    this.error = '';
    this.loading = true;

    const body = {
      type: this.type,
      url: this.url.trim() || undefined,
      scrapedTitle: this.scrapedTitle.trim() || undefined,
      scrapedDescription: this.scrapedDescription.trim() || undefined,
    };

    try {
      const result = await createGeneration(body);
      this.dispatchEvent(
        new CustomEvent('generation-created', {
          detail: { generationId: result.generationId },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 402) {
          this.error = 'Sin créditos disponibles. Adquiere más créditos para continuar.';
        } else if (err.status === 422) {
          this.error = err.message + ' Rellena el título y la descripción manualmente.';
        } else {
          this.error = err.message;
        }
      } else {
        this.error = 'Error inesperado. Inténtalo de nuevo.';
      }
    } finally {
      this.loading = false;
    }
  }

  render() {
    const isEcommerce = this.type === 'e-commerce';

    return html`
      <form @submit=${(e: Event) => { e.preventDefault(); void this.submit(); }}>
        <ui-select
          label="Tipo de contenido"
          name="type"
          .value=${this.type}
          .options=${this.typeOptions}
          @value-changed=${this.handleTypeChange}
        ></ui-select>

        ${isEcommerce
          ? html`
            <div class="field-group">
              <ui-input
                label="URL del producto"
                type="url"
                name="url"
                placeholder="https://tienda.com/producto/ejemplo"
                .value=${this.url}
                .maxlength=${500}
                required
                @value-changed=${this.handleUrlChange}
              ></ui-input>

              <div class="divider">Metadatos (opcional)</div>

              <div class="hint-block">
                Si la URL es pública, los extraemos automáticamente.
                Rellena <strong>ambos campos</strong> solo si quieres sobreescribir los datos.
              </div>

              <ui-input
                label="Título del producto"
                name="scrapedTitle"
                placeholder="Nombre del producto"
                .value=${this.scrapedTitle}
                .maxlength=${200}
                @value-changed=${this.handleTitleChange}
              ></ui-input>

              <ui-input
                label="Descripción del producto"
                name="scrapedDescription"
                placeholder="Breve descripción del producto"
                .value=${this.scrapedDescription}
                .maxlength=${1000}
                @value-changed=${this.handleDescriptionChange}
              ></ui-input>
            </div>
          `
          : html`
            <div class="field-group">
              <div class="hint-block">
                Para contenido <strong>general</strong>, introduce el tema manualmente.
                La URL es opcional (como referencia).
              </div>

              <ui-input
                label="Título / Tema"
                name="scrapedTitle"
                placeholder="Ej: Los mejores auriculares para gaming en 2026"
                .value=${this.scrapedTitle}
                .maxlength=${200}
                required
                @value-changed=${this.handleTitleChange}
              ></ui-input>

              <ui-input
                label="Descripción"
                name="scrapedDescription"
                placeholder="Describe el contenido que quieres generar"
                .value=${this.scrapedDescription}
                .maxlength=${1000}
                required
                @value-changed=${this.handleDescriptionChange}
              ></ui-input>

              <ui-input
                label="URL de referencia (opcional)"
                type="url"
                name="url"
                placeholder="https://..."
                .value=${this.url}
                .maxlength=${500}
                @value-changed=${this.handleUrlChange}
              ></ui-input>
            </div>
          `}

        ${this.error
          ? html`<div class="alert alert--error">${this.error}</div>`
          : ''}

        <ui-button
          fullwidth
          ?loading=${this.loading}
          ?disabled=${this.loading}
          @ui-click=${() => void this.submit()}
        >
          ${this.loading ? 'Creando generación…' : 'Crear generación'}
        </ui-button>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'content-create-form': ContentCreateForm;
  }
}
